import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'api/axiosInstance';
import { useSnackbar } from 'contexts/SnackbarContext';
import { useCompany } from 'contexts/CompanyContext';

// material-ui
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';

// third-party
import * as Yup from 'yup';
import { Formik } from 'formik';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// project imports
import IconButton from 'components/@extended/IconButton';
import AnimateButton from 'components/@extended/AnimateButton';
import endpoints from 'api/endpoints';

import { strengthColor, strengthIndicator, getPasswordRequirements } from 'utils/password-strength';

// assets
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import CameraOutlined from '@ant-design/icons/CameraOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';

// site templates
import { getTemplatesForIndustry, getTemplateKey, downloadTemplate } from 'data/site-templates';

// ============================|| JWT - REGISTER ||============================ //

export default function AuthRegister({ onSuccess }) {
  const { showSnackbar } = useSnackbar();
  const { fetchCompanies } = useCompany();
  const [level, setLevel] = useState();
  const [passwordRequirements, setPasswordRequirements] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newIndustryDescription, setNewIndustryDescription] = useState('');
  const [newIndustryIcon, setNewIndustryIcon] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const [formikSetFieldValue, setFormikSetFieldValue] = useState(null);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [submittingIndustry, setSubmittingIndustry] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [dominantColor, setDominantColor] = useState('');
  const [siteContentFile, setSiteContentFile] = useState(null);
  const [brandingDocFile, setBrandingDocFile] = useState(null);

  // Function to extract dominant color from image
  const extractDominantColor = (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Count color occurrences
        const colorCount = {};
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          // Skip transparent pixels and very light colors (likely background)
          if (a < 128) continue;
          if (r > 240 && g > 240 && b > 240) continue;

          // Round to reduce color variations
          const roundedR = Math.round(r / 10) * 10;
          const roundedG = Math.round(g / 10) * 10;
          const roundedB = Math.round(b / 10) * 10;

          const hex = `#${roundedR.toString(16).padStart(2, '0')}${roundedG.toString(16).padStart(2, '0')}${roundedB.toString(16).padStart(2, '0')}`;
          colorCount[hex] = (colorCount[hex] || 0) + 1;
        }

        // Find the most common color
        let maxCount = 0;
        let dominant = '#000000';
        for (const [color, count] of Object.entries(colorCount)) {
          if (count > maxCount) {
            maxCount = count;
            dominant = color;
          }
        }

        resolve(dominant);
      };
      img.onerror = () => resolve('#000000');
      img.src = imageDataUrl;
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const changePassword = (value) => {
    const temp = strengthIndicator(value);
    setLevel(strengthColor(temp));
    setPasswordRequirements(getPasswordRequirements(value));
  };

  const fetchIndustries = async () => {
    try {
      setLoadingIndustries(true);

      // Get token from localStorage
      const token = localStorage.getItem('token');

      // Make API call with token
      const response = await axios.get(endpoints.registration.industries, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      console.log('Industries fetched:', response.data);
      if (Array.isArray(response.data)) {
        setCompanyOptions(response.data);
      } else if (response.data.results && Array.isArray(response.data.results)) {
        // If API returns paginated data
        setCompanyOptions(response.data.results);
      } else if (response.data.data && Array.isArray(response.data.data)) {
        setCompanyOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
      showSnackbar(error.response?.data?.message || error.message || 'Failed to fetch industries', 'error');
      // Set some default options in case of error
      setCompanyOptions([
        { id: 1, name: 'Technology' },
        { id: 2, name: 'Finance' },
        { id: 3, name: 'Healthcare' }
      ]);
    } finally {
      setLoadingIndustries(false);
    }
  };

  const handleOpenModal = (setFieldValue) => {
    setFormikSetFieldValue(() => setFieldValue);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewCompanyName('');
    setNewIndustryDescription('');
    setNewIndustryIcon('');
    setIconFile(null);
  };

  const generateCode = (name) => {
    // Generate a random code based on name and timestamp
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 15);
    return `${name.substring(0, 3).toUpperCase()}_${timestamp}_${randomStr}`.substring(0, 32);
  };

  const handleIconChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIconFile(file);
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewIndustryIcon(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = async () => {
        const imageDataUrl = reader.result;
        setLogoPreview(imageDataUrl);
        // Extract dominant color from the logo
        const color = await extractDominantColor(imageDataUrl);
        setDominantColor(color);
        console.log('Dominant color extracted:', color);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddNewCompany = async () => {
    if (newCompanyName.trim()) {
      try {
        setSubmittingIndustry(true);

        // Get token from localStorage
        const token = localStorage.getItem('token');

        // Generate code
        const code = generateCode(newCompanyName);

        // Prepare payload
        const payload = {
          name: newCompanyName.trim(),
          code: code,
          description: newIndustryDescription.trim() || '',
          icon: newIndustryIcon || ''
        };

        console.log('Creating industry with payload:', payload);

        // Call the create industry API with token
        const response = await axios.post(endpoints.registration.createIndustry, payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }); 

        console.log('New industry created:', response.data);

        // Add the new industry to the list
        const newIndustry = response.data;
        setCompanyOptions([...companyOptions, newIndustry]);

        if (formikSetFieldValue) {
          // Set the value based on API response structure
          formikSetFieldValue('company', newIndustry.id || newIndustry.name || newCompanyName.trim());
        }

        showSnackbar('Industry created successfully', 'success');
        handleCloseModal();
      } catch (error) {
        console.error('Error creating industry:', error);
        showSnackbar(error.response?.data?.message || error.message || 'Failed to create industry', 'error');
      } finally {
        setSubmittingIndustry(false);
      }
    }
  };

  const handleRegister = async (values, { setErrors, setStatus, setSubmitting }) => {
    try {
      setRegistering(true);

      // Get token from localStorage
      const token = localStorage.getItem('token');

      console.log('values', values);

      // Create FormData to send file
      const formData = new FormData();
      formData.append('company_name', values.firstname);
      formData.append('industry_code', String(values.company || ''));
      formData.append('contact_email', values.email);
      formData.append('contact_phone', values.contact_phone);
      formData.append('address', values.address);
      formData.append('admin_username', values.firstname);
      formData.append('admin_email', values.email);
      formData.append('admin_password', values.password);
      formData.append('admin_first_name', values.firstname);
      formData.append('branding_colour', dominantColor || '');

      // Append logo file if exists
      if (logoFile) {
        formData.append('branding_logo', logoFile);
      }

      // Append site content doc if exists
      if (siteContentFile) {
        formData.append('site_content_doc', siteContentFile);
      }

      // Append branding doc if exists
      if (brandingDocFile) {
        formData.append('branding_doc', brandingDocFile);
      }

      console.log('Registering user with FormData');

      // Call the create tenant API
      const response = await axios.post(endpoints.registration.createTentants, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('Registration successful:', response.data);

      setStatus({ success: true });
       const companiesData = await fetchCompanies();
      console.log('Companies data in handleRegister:', companiesData);
      setSubmitting(false);
      showSnackbar('Registration successful!', 'success');

      // Close the modal if callback is provided
      if (onSuccess) {
        onSuccess();
      }

      // You can redirect or handle successful registration here
      // window.location.href = '/login';
    } catch (error) {
      console.error('Registration error:', error);
      setStatus({ success: false });
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed. Please try again.';
      setErrors({ submit: errorMessage });
      showSnackbar(errorMessage, 'error');
      setSubmitting(false);
    } finally {
      setRegistering(false);
    }
  };

  useEffect(() => {
    changePassword('');
    fetchIndustries();
  }, []);

  const validationSchema = Yup.object().shape({
    firstname: Yup.string().required('Company name is required'),
    email: Yup.string().email('Must be a valid email').required('Email is required'),
    contact_phone: Yup.string()
      .required('Phone number is required')
      .test('is-valid-phone', 'Phone number must be maximum 10 digits', (value) => {
        if (!value) return false;
        // Remove country code and non-digit characters to get just the number
        const digitsOnly = value.replace(/\D/g, '');
        // For India (+91), the digits would be 91 + 10 digits = 12 total
        // Get the last 10 digits (excluding country code)
        const numberWithoutCountryCode = digitsOnly.slice(-10);
        return numberWithoutCountryCode.length <= 10 && isValidPhoneNumber(value);
      }),
    address: Yup.string().required('Address is required'),
    company: Yup.string().required('Industry is required'),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .max(12, 'Password must be at most 12 characters')
      .matches(/[0-9]/, 'Password must include at least one number')
      .matches(/[a-z]/, 'Password must include lowercase letters')
      .matches(/[A-Z]/, 'Password must include uppercase letters')
  });

  return (
    <>
      <Formik
        initialValues={{
          firstname: '',
          lastname: '',
          contact_phone: '',
          address: '',
          email: '',
          company: '',
          password: '',
          submit: null
        }}
        validationSchema={validationSchema}
        onSubmit={handleRegister}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values, setFieldValue }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 6, md: 6 }}>
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="firstname-signup">Company Name*</InputLabel>
                  <OutlinedInput
                    id="firstname-login"
                    type="firstname"
                    value={values.firstname}
                    name="firstname"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Acme Corp"
                    fullWidth
                    error={Boolean(touched.firstname && errors.firstname)}
                  />
                </Stack>
                {touched.firstname && errors.firstname && (
                  <FormHelperText error id="helper-text-firstname-signup">
                    {errors.firstname}
                  </FormHelperText>
                )}
              </Grid>
                            <Grid size={6}>
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="company-signup">Industry</InputLabel>
                  <Select
                    fullWidth
                    id="company-signup"
                    name="company"
                    value={values.company}
                    onChange={(e) => {
                      if (e.target.value === 'add_new') {
                        handleOpenModal(setFieldValue);
                      } else {
                        handleChange(e);
                      }
                    }}
                    onBlur={handleBlur}
                    error={Boolean(touched.company && errors.company)}
                    displayEmpty
                    disabled={loadingIndustries}
                    sx={{
                      '& .MuiSelect-select': {
                        padding: '10.5px 14px'
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>{loadingIndustries ? 'Loading industries...' : 'Select an industry'}</em>
                    </MenuItem>
                    <MenuItem
                      value="add_new"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'primary.lighter'
                        }
                      }}
                    >
                      + Add New
                    </MenuItem>
                    {console.log('companyOptions:', companyOptions)}
                    {companyOptions.map((industry) => (
                      <MenuItem
                        key={industry.id || industry.name || industry}
                        value={industry.code || industry.name || industry}
                      >
                        {industry.name || industry}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
                {touched.company && errors.company && (
                  <FormHelperText error id="helper-text-company-signup">
                    {errors.company}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={12}>
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="address-signup">Company Address*</InputLabel>
                  <OutlinedInput
                    fullWidth
                    error={Boolean(touched.address && errors.address)}
                    id="address-signup"
                    type="text"
                    value={values.address}
                    name="address"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="123 Main Street, City, State, ZIP"
                    multiline
                    rows={2}
                  />
                </Stack>
                {touched.address && errors.address && (
                  <FormHelperText error id="helper-text-address-signup">
                    {errors.address}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={12}>
  <Stack sx={{ gap: 1 }}>
    <InputLabel htmlFor="contact-phone-signup">Company Phone Number*</InputLabel>
    <PhoneInput
      id="contact-phone-signup"
      international
      defaultCountry="IN"
      limitMaxLength
      value={values.contact_phone}
      onChange={(value) => setFieldValue('contact_phone', value)}
      onBlur={handleBlur}
      placeholder="Enter phone number"
      className="phone-input-field"
      style={{
        width: '100%',
        padding: '10.5px 14px',
        fontSize: '1rem',
        fontFamily: 'inherit',
        borderRadius: '4px',
        border: `1px solid ${touched.contact_phone && errors.contact_phone ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)'}`,
        transition: 'border-color 0.2s',
      }}
    />
  </Stack>
  {touched.contact_phone && errors.contact_phone && (
    <FormHelperText error id="helper-text-contact-phone-signup">
      {errors.contact_phone}
    </FormHelperText>
  )}
</Grid>

              {/* Company Logo Upload */}
              <Grid size={12}>
                <Stack sx={{ gap: 1 }}>
                  <InputLabel>Company Logo</InputLabel>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'grey.50'
                    }}
                  >
                    {/* Logo Preview */}
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'background.paper',
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <CameraOutlined style={{ fontSize: 24, color: '#8c8c8c' }} />
                      )}
                    </Box>

                    {/* Upload/Remove Buttons */}
                    <Stack sx={{ flex: 1 }} spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {logoFile ? logoFile.name : 'Upload your company logo (PNG, JPG)'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          component="label"
                          startIcon={<CameraOutlined />}
                        >
                          {logoPreview ? 'Change' : 'Upload'}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleLogoChange}
                          />
                        </Button>
                        {logoPreview && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlined />}
                            onClick={() => {
                              setLogoFile(null);
                              setLogoPreview('');
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              {/* Site Content & Branding Doc Uploads */}
              <Grid size={12}>
                <Stack sx={{ gap: 1.5 }}>
                  <InputLabel>Site Layout & Branding Documents</InputLabel>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                    Upload JSON templates to customize the tenant's landing page. Download a sample template for the selected industry, fill in your content, then upload.
                  </Typography>

                  {/* Download template buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<DownloadOutlined />}
                      onClick={() => {
                        const selectedIndustry = companyOptions.find(opt => (opt.code || opt.name) === values.company);
                        const templates = getTemplatesForIndustry(selectedIndustry?.name || '');
                        downloadTemplate(templates.content, `site-content-template.json`);
                      }}
                    >
                      Download Site Content Template
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<DownloadOutlined />}
                      onClick={() => {
                        const selectedIndustry = companyOptions.find(opt => (opt.code || opt.name) === values.company);
                        const templates = getTemplatesForIndustry(selectedIndustry?.name || '');
                        downloadTemplate(templates.branding, `branding-guide-template.json`);
                      }}
                    >
                      Download Branding Guide Template
                    </Button>
                  </Box>

                  {/* File upload row */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {/* Site Content Doc */}
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        startIcon={<FileTextOutlined />}
                        sx={{ justifyContent: 'flex-start', height: 48, textTransform: 'none' }}
                      >
                        {siteContentFile ? siteContentFile.name : 'Upload Site Content (JSON)'}
                        <input
                          type="file"
                          hidden
                          accept=".json,application/json"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) setSiteContentFile(file);
                          }}
                        />
                      </Button>
                      {siteContentFile && (
                        <Button size="small" color="error" onClick={() => setSiteContentFile(null)} sx={{ mt: 0.5 }}>
                          Remove
                        </Button>
                      )}
                    </Box>

                    {/* Branding Doc */}
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        startIcon={<FileTextOutlined />}
                        sx={{ justifyContent: 'flex-start', height: 48, textTransform: 'none' }}
                      >
                        {brandingDocFile ? brandingDocFile.name : 'Upload Branding Guide (JSON)'}
                        <input
                          type="file"
                          hidden
                          accept=".json,application/json"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) setBrandingDocFile(file);
                          }}
                        />
                      </Button>
                      {brandingDocFile && (
                        <Button size="small" color="error" onClick={() => setBrandingDocFile(null)} sx={{ mt: 0.5 }}>
                          Remove
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Grid>

              <Grid size={12}>
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="email-signup">Company Email Address*</InputLabel>
                  <OutlinedInput
                    fullWidth
                    error={Boolean(touched.email && errors.email)}
                    id="email-login"
                    type="email"
                    value={values.email}
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="demo@company.com"
                  />
                </Stack>
                {touched.email && errors.email && (
                  <FormHelperText error id="helper-text-email-signup">
                    {errors.email}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={12}>
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="password-signup">Password</InputLabel>
                  <OutlinedInput
                    fullWidth
                    error={Boolean(touched.password && errors.password)}
                    id="password-signup"
                    type={showPassword ? 'text' : 'password'}
                    value={values.password}
                    name="password"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      changePassword(e.target.value);
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          color="secondary"
                        >
                          {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                    placeholder="******"
                  />
                </Stack>
                {touched.password && errors.password && (
                  <FormHelperText error id="helper-text-password-signup">
                    {errors.password}
                  </FormHelperText>
                )}
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <Stack spacing={0.5}>
                    {passwordRequirements.map((req, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {req.met ? (
                          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: '16px' }} />
                        )}
                        <Typography variant="body2" fontSize="0.75rem" color={req.met ? 'success.main' : 'text.secondary'}>
                          {req.label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <Typography variant="body2">
                  By Signing up, you agree to our &nbsp;
                  <Link href="https://mui.com/store/terms/" variant="subtitle2" target="_blank">
                    Terms of Service
                  </Link>
                  &nbsp; and &nbsp;
                  <Link href="https://mui.com/legal/privacy/" variant="subtitle2" target="_blank">
                    Privacy Policy
                  </Link>
                </Typography>
              </Grid>
              {errors.submit && (
                <Grid size={12}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Grid>
              )}
              <Grid size={12}>
                <AnimateButton>
                  <Button
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={registering}
                  >
                    {registering ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </AnimateButton>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>

      {/* Add New Industry Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h4">Add New Industry</Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {/* Industry Name */}
            <TextField
              autoFocus
              label="Industry Name *"
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              placeholder="Enter industry name"
              required
            />

            {/* Description */}
            <TextField
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={newIndustryDescription}
              onChange={(e) => setNewIndustryDescription(e.target.value)}
              placeholder="Enter industry description"
            />

            {/* Icon Upload */}
            <Box>
              <InputLabel sx={{ mb: 1 }}>Industry Icon</InputLabel>
              <Button variant="outlined" component="label" fullWidth sx={{ justifyContent: 'flex-start', height: '56px' }}>
                {iconFile ? iconFile.name : 'Choose Icon File'}
                <input type="file" hidden accept="image/*" onChange={handleIconChange} />
              </Button>
              {newIndustryIcon && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <img
                    src={newIndustryIcon}
                    alt="Icon preview"
                    style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} color="secondary" variant="outlined" disabled={submittingIndustry}>
            Cancel
          </Button>
          <Button
            onClick={handleAddNewCompany}
            variant="contained"
            color="primary"
            disabled={!newCompanyName.trim() || submittingIndustry}
          >
            {submittingIndustry ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

AuthRegister.propTypes = {
  onSuccess: PropTypes.func
};
